import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { DateTimeInput } from 'semantic-ui-calendar-react'
import { Form, Progress, Tab } from 'semantic-ui-react'
import { useAuth } from '~/routes/components/AuthProvider'
import { StyledMenuItem, StyledTabPane } from './style'
import Layout from '~/components/Layout'
import { useNavigate, useParams } from 'react-router-dom'
import { RouteTypes } from '~/routes/constants'
import { El } from '~/components/Layout/style'
import notify from '~/components/Notify'
import { LoginPopupsService } from '~/api/services/loginPopups'
import { Controller, useForm } from 'react-hook-form'
import { FormFields } from '~/components/FormFields'
import axios from 'axios'
import ENV from '~/environment';
import dayjs from 'dayjs'
import LanguageTab from './LanguageTab'
import { ProgressWrapper } from '../../CloudStorage/style'
import { formatNumberWithoutFractions, getDateWithTime, getMaxDate, getMinDate, handleErrors } from '~/utils/helpers'
import Dialog from '~/components/Dialog'
import { StyledErrorLabel } from '../../MilkMoney/MilkDelivery/ShowTours/style'
import { getError } from '~/components/FormFields/util'

const CreateEditLoginPopup: React.FunctionComponent = () => {
  const { systemData: { languages, logged: { languageId } } } = useAuth()
  const [optionsData, setOptionsData] = useState(null)
  const { t } = useTranslation()
  const navigate = useNavigate()
  const languageLocal = languages.find(language => language.id === languageId)
  const { id } = useParams()
  const [isUploading, setIsUploading] = useState(false)
  const [isBusy, setIsBusy] = useState(false)
  const [activeIndex, setActiveIndex] = useState(0)
  const [uploadProgress, setUploadProgress] = useState(0);
  const [abortController, setAbortController] = useState<AbortController | null>(null);

  const {
    handleSubmit,
    control,
    setValue,
    watch,
    getValues,
    formState,
    formState: { isSubmitted },
    reset,
    clearErrors
  } = useForm({
    mode: 'onSubmit'
  });

  const watchDateFrom = watch('from')
  const watchDateTo = watch('to')
  const watchStatus = watch('status')

  useEffect(() => {
    const controller = new AbortController();
    fetchData(controller.signal);

    return () => {
      controller.abort();
    };
  }, []);

  async function getFileUrls(id, languages) {
    for (const language of languages) {
      try {
        const response = await LoginPopupsService.getFileUrl(id, { fileName: language.content });
        language.fileEdit = {
          src: response.data,
          type: language.content.split('.')[1],
          filename: language.content
        };
      } catch (error) {
        // do nothing
      }
    }
    return languages
  }

  async function fetchData(signal: AbortSignal) {
    setIsBusy(true)
    try {
      const responseOptions = await LoginPopupsService.getOptionsData(signal)
      setOptionsData(responseOptions.data)
      if (id) {
        const response = await LoginPopupsService.getLoginPopup(id, signal)
        response.data.from = response.data.from ? dayjs(response.data.from).format('DD.MM.YYYY HH:mm') : ''
        response.data.to = response.data.to ? dayjs(response.data.to).format('DD.MM.YYYY HH:mm') : ''
        const languagesEdit = response.data.languages
        languages.map((language) => {
          const indexLang = languagesEdit.findIndex(languageEd => languageEd.id === language.id)
          if (indexLang < 0) {
            languagesEdit.push({
              id: language.id,
              headerText: '',
              footerText: '',
              file: '',
              title: '',
              order: language.order,
              label: language.label
            })
          } else {
            languagesEdit[indexLang].order = language.order
          }
        })
        languagesEdit.sort((a, b) => a.order - b.order)
        languagesEdit.forEach(lang => delete lang.order)
        response.data.languages = await getFileUrls(id, languagesEdit)
        reset(response.data)
      } else {
        const languagesNew = []
        languages.map((language) => {
          languagesNew.push({
            id: language.id,
            headerText: '',
            footerText: '',
            file: '',
            title: '',
            label: language.label
          })
        })
        setValue('languages', languagesNew)
      }
    } catch (error) {
      if (error.response?.data) {
        notify(error.response.data.message, 'error')
      } else {
        notify(t('General error'), 'error')
      }
    }
    setIsBusy(false)
  }

  function getMenuItem(pane, index) {
    const languagesErrors = formState.errors?.languages;
    const paneHasError = index in (languagesErrors || {});

    return <StyledMenuItem
      key={pane}
      className='tab-item'
    >
      <div className={`${paneHasError ? 'error' : ''}`}>{pane}</div>
    </StyledMenuItem>
  }

  function renderLanguageFields() {
    const langArray = getValues('languages') || languages
    return <Tab
      menu={{ pointing: true }}
      renderActiveOnly={false}
      onTabChange={(_, data) => setActiveIndex(data.activeIndex as number)}
      style={{ marginTop: '1.2em' }}
      panes={langArray.map((language, index) => {
        return {
          menuItem: getMenuItem(language.label, index),
          pane: !isBusy && <StyledTabPane key={language.label}>
            <LanguageTab
              control={control}
              index={index}
              activeIndex={activeIndex}
              setValue={setValue}
              getValues={getValues}
              clearErrors={clearErrors}
              languageId={language.id.toString()}
              watchStatus={Boolean(watchStatus)}
            />
          </StyledTabPane>
        }
      })}
    />
  }

  function renderForm() {
    return <>
      <FormFields.Checkbox
        control={control}
        defaultValue={0}
        checkboxValue={1}
        label={t('Pop-up enabled for farmers?')}
        name={'status'}
      />
      <FormFields.Input
        control={control}
        name={'name'}
        placeholder={t('Name')}
        label={t('Name')}
        rules={{
          required: t('Please enter name'),
          maxLength: {
            value: 100,
            message: t('Max. 100 characters allowed')
          }
        }}
      />
      <Form.Group widths={'equal'}>
        <Controller
          control={control}
          name={'from'}
          rules={{
            required: watchStatus ? t('Please enter Date from') : false,
            validate: () => {
              const dateFrom = watchDateFrom ? getDateWithTime(watchDateFrom) : ''
              const currentDate = getDateWithTime(dayjs(new Date()).format('DD.MM.YYYY HH:mm'))
              if (dateFrom && currentDate && dayjs(dateFrom).diff(dayjs(currentDate)) <= 0) {
                return t('Please select a date ahead of the current date.')
              }
              return true
            }
          }}
          render={({ fieldState }) => {
            const dateFrom = watchDateFrom ? getDateWithTime(watchDateFrom) : ''
            const currentDate = getDateWithTime(dayjs(new Date()).format('DD.MM.YYYY HH:mm'))
            return <Form.Field
              required
              error={!watchDateFrom && isSubmitted ? getError(fieldState) : dateFrom && currentDate && dayjs(dateFrom).diff(dayjs(currentDate)) <= 0 ? true : false}
            >
              <label>{t('Date from')}</label>
              <DateTimeInput
                name='dateTime'
                placeholder={t('Date from')}
                minDate={!id ? dayjs(new Date()).format('DD.MM.YYYY HH:mm') : ''}
                closable
                markColor={'blue'}
                localization={languageLocal.code}
                closeOnMouseLeave={false}
                initialDate={dayjs(new Date()).format('DD.MM.YYYY HH:mm')}
                dateTimeFormat={'DD.MM.YYYY HH:mm'}
                className={'datePicker'}
                value={watchDateFrom || ''}
                iconPosition='left'
                onChange={(event, { value }) => setValue('from', value)}
              />
              {!watchDateFrom && isSubmitted && <StyledErrorLabel className='ui pointing basic label'>{t('Please enter date from')}</StyledErrorLabel>}
              {dateFrom && currentDate && dayjs(dateFrom).diff(dayjs(currentDate)) <= 0 &&
                <StyledErrorLabel className='ui pointing basic label'>{t('Please select a date ahead of the current date.')}</StyledErrorLabel>}
            </Form.Field>
          }}
        />
        <Controller
          control={control}
          name={'to'}
          rules={{
            required: watchStatus ? t('Please enter Date to') : false
          }}
          render={({ fieldState }) => {
            return <Form.Field
              required
              error={!watchDateTo && isSubmitted ? getError(fieldState) : false}
            >
              <label>{t('Date to')}</label>
              <DateTimeInput
                name='dateTime'
                className={'datePicker'}
                placeholder={t('Date to')}
                closable
                markColor={'blue'}
                initialDate={getMinDate(watchDateFrom)}
                minDate={!id ? getMinDate(watchDateFrom) : ''}
                maxDate={getMaxDate(watchDateFrom)}
                localization={languageLocal.code}
                closeOnMouseLeave={false}
                dateTimeFormat={'DD.MM.YYYY HH:mm'}
                value={watchDateTo || ''}
                iconPosition='left'
                onChange={(event, { value }) => setValue('to', value)}
              />
              {!watchDateTo && isSubmitted && <StyledErrorLabel className='ui pointing basic label'>{t('Please enter date from')}</StyledErrorLabel>}
            </Form.Field>

          }}
        />
      </Form.Group>
      <FormFields.Select
        label={t('Farmer groups')}
        control={control}
        name={'farmerGroups'}
        rules={{
          required: watchStatus ? t('Please select farmer group') : false
        }}
        placeholder={t('Select farmer group')}
        options={optionsData?.farmerGroups.map(farmerGroup => ({ value: farmerGroup.id, text: farmerGroup.name })) || []}
        multiple
        selectOnBlur={false}
        selection
      />
      {!isBusy && renderLanguageFields()}
      <FormFields.Checkbox
        control={control}
        label={t('Can file be downloaded?')}
        defaultValue={0}
        checkboxValue={1}
        name={'downloadEnabled'}
      />
    </>
  }

  function convertDate(date) {
    if (date && date !== '') {
      const [day, month, yearAndTime] = date.split('.')
      const year = yearAndTime?.split(' ')[0]
      const time = yearAndTime?.split(' ')[1]
      return (`${year}-${month}-${day} ${time}`)
    }
    return date
  }

  async function onSubmit(data) {
    const formData = new FormData();
    formData.append('status', data.status)
    formData.append('name', data.name)
    if (data.from) {
      formData.append('from', convertDate(data.from.toString()))
    }
    if (data.to) {
      formData.append('to', convertDate(data.to.toString()))
    }
    formData.append('farmerGroups', JSON.stringify(data.farmerGroups))
    formData.append('downloadEnabled', data.downloadEnabled)
    let hasFiles = false;
    data.languages.forEach(language => {
      delete data.languages.fileEdit
      if (language.file) {
        hasFiles = true
        formData.append(`file-${language.id}`, language.file);
        delete language.file
      }
    });
    formData.append('languages', JSON.stringify(data.languages));
    const url = id ? `/settings/pop-ups/${id}/update` : '/settings/pop-ups/store'
    const controller = new AbortController();
    setAbortController(controller);
    setIsUploading(hasFiles)
    setIsBusy(!hasFiles)
    try {
      await axios.post(
        `${ENV.API_URL}${url}`,
        formData,
        {
          headers: { 'Authorization': localStorage.getItem('token') },
          onUploadProgress: (progressEvent) => {
            const { loaded, total } = progressEvent;
            setUploadProgress((loaded / total) * 100);
          },
          signal: controller.signal
        });
      const successMessage = id ? t('Successfully updated login popup') : t('Successfully created login popup')
      notify(successMessage, 'success')
      setIsUploading(false)
      setIsBusy(false)
      navigate(RouteTypes.SETTINGS_LOGIN_POPUP)
    } catch (error) {
      if (axios.isCancel(error)) {
        notify(t('Upload canceled'), 'error');
      } else {
        handleErrors(error)
      }
    }
    setIsBusy(false)
    setIsUploading(false)
  }

  function renderActions() {
    return <El.PageHeaderActionButton
      disabled={isUploading}
    >{id ? t('Update') : t('Save')}</El.PageHeaderActionButton>
  }

  function renderProgressBar() {
    const uploadPercent = `${formatNumberWithoutFractions(uploadProgress)}%`
    return <ProgressWrapper>
      <Progress percent={uploadProgress} indicating className='custom-progress' />
      <p className='text-below-progress'>{t('Uploaded: {{uploadPercent}}', { uploadPercent })}</p>
    </ProgressWrapper>
  }

  function renderUpload() {

    return <Dialog
      open={Boolean(isUploading)}
      textHeader={t('Upload')}
      htmlContent={false}
      textContent={<div style={{ position: 'relative' }}>
        {renderProgressBar()}
      </div>}
      contentAutoSize
      textClose={t('Cancel')}
      handleClose={() => {
        if (abortController) {
          abortController.abort();
        }
        setIsUploading(false)
      }}
    />
  }

  return <Layout
    title={id ? t('Update login popup') : t('Create login popup')}
    isBusy={isBusy}
    segmented
    close={RouteTypes.SETTINGS_LOGIN_POPUP}
    actions={renderActions()}
    onSubmit={handleSubmit(onSubmit)}
  >
    {renderForm()}
    {isUploading && uploadProgress > 0 && renderUpload()}
  </Layout>

}
export default CreateEditLoginPopup
